/**
 * Card Component
 * A flexible container component for grouping related content with consistent styling
 */

import React from 'react';
import styled, { css } from 'styled-components';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'flat';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';

export interface CardProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Visual style variant */
  variant?: CardVariant;
  /** Padding size inside the card */
  padding?: CardPadding;
  /** Whether the card should be clickable/interactive */
  interactive?: boolean;
  /** Optional header content */
  header?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Main card content */
  children: React.ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

const CardContainer = styled.div<{
  $variant: CardVariant;
  $padding: CardPadding;
  $interactive: boolean;
}>`
  /* Base styles */
  background: var(--surface);
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;

  /* Variant styles */
  ${(props) =>
    props.$variant === 'default' &&
    css`
      border: 1px solid var(--border);
      box-shadow: none;
    `}

  ${(props) =>
    props.$variant === 'elevated' &&
    css`
      border: 1px solid var(--border);
      box-shadow: var(--shadow-medium);
    `}

  ${(props) =>
    props.$variant === 'outlined' &&
    css`
      border: 2px solid var(--border);
      box-shadow: none;
    `}

  ${(props) =>
    props.$variant === 'flat' &&
    css`
      border: none;
      box-shadow: none;
      background: transparent;
    `}

  /* Interactive styles */
  ${(props) =>
    props.$interactive &&
    css`
      cursor: pointer;

      &:hover {
        box-shadow: none;
        border-color: var(--brand-600);
      }

      &:active {
      }

      &:focus-visible {
        outline: 3px solid var(--brand-600);
        outline-offset: 2px;
      }
    `}

  /* Padding variants */
  ${(props) =>
    props.$padding === 'none' &&
    css`
      padding: 0;
    `}

  ${(props) =>
    props.$padding === 'sm' &&
    css`
      padding: var(--space-md);
    `}

  ${(props) =>
    props.$padding === 'md' &&
    css`
      padding: var(--space-lg);
    `}

  ${(props) =>
    props.$padding === 'lg' &&
    css`
      padding: var(--space-xl);
    `}

  ${(props) =>
    props.$padding === 'xl' &&
    css`
      padding: var(--space-2xl);
    `}

  /* Responsive adjustments */
  @media (max-width: 768px) {
    ${(props) =>
      props.$padding === 'lg' &&
      css`
        padding: var(--space-lg);
      `}

    ${(props) =>
      props.$padding === 'xl' &&
      css`
        padding: var(--space-xl);
      `}
  }

  @media (max-width: 480px) {
    ${(props) =>
      props.$padding === 'md' &&
      css`
        padding: var(--space-md);
      `}

    ${(props) =>
      props.$padding === 'lg' &&
      css`
        padding: var(--space-md);
      `}

    ${(props) =>
      props.$padding === 'xl' &&
      css`
        padding: var(--space-lg);
      `}
  }
`;

const CardHeader = styled.div`
  padding-bottom: var(--space-lg);
  border-bottom: 1px solid var(--border);
  margin-bottom: var(--space-lg);

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const CardContent = styled.div`
  /* Content inherits card padding, this is just for semantic structure */
`;

const CardFooter = styled.div`
  padding-top: var(--space-lg);
  border-top: 1px solid var(--border);
  margin-top: var(--space-lg);

  &:first-child {
    padding-top: 0;
    border-top: none;
    margin-top: 0;
  }
`;

// ============================================================================
// Component
// ============================================================================

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      interactive = false,
      header,
      footer,
      children,
      role,
      tabIndex,
      onClick,
      onKeyDown,
      ...rest
    },
    ref,
  ) => {
    // Handle keyboard activation for interactive cards
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        interactive &&
        onClick &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        // Create a synthetic mouse event for the onClick handler
        const syntheticEvent = {
          ...event,
          type: 'click',
          target: event.target,
          currentTarget: event.currentTarget,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          preventDefault: () => {},
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          stopPropagation: () => {},
        } as unknown as React.MouseEvent<HTMLDivElement>;
        onClick(syntheticEvent);
      }
      onKeyDown?.(event);
    };

    // Set appropriate ARIA attributes for interactive cards
    const accessibilityProps = interactive
      ? {
          role: role || 'button',
          tabIndex: tabIndex ?? 0,
          'aria-pressed': undefined, // Can be overridden via rest props
        }
      : {
          role,
          tabIndex,
        };

    return (
      <CardContainer
        ref={ref}
        $variant={variant}
        $padding={padding}
        $interactive={interactive}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...accessibilityProps}
        {...rest}
      >
        {header && <CardHeader>{header}</CardHeader>}

        <CardContent>{children}</CardContent>

        {footer && <CardFooter>{footer}</CardFooter>}
      </CardContainer>
    );
  },
);

Card.displayName = 'Card';
